import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardTable from '../components/dashboard/DashboardTable';
import { paymentsApi, PaymentTrackingUISearchResultModel } from '../services/api/paymentsApi';

const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'client' | 'beneficiary'>('client');
  const [results, setResults] = useState<PaymentTrackingUISearchResultModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Handle search form submission
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      setError('Please enter a search term');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setHasSearched(true);
    
    try {
      // Configure search parameters based on search type
      const searchParams = {
        paymentTrackingUISearchType: 'searchBox',
        ...(searchType === 'client' 
          ? { aggregateId: searchQuery } 
          : { extPaymentId: searchQuery })
      };
      
      const data = await paymentsApi.searchPayments(searchParams);
      setResults(data);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to perform search. Please try again later.');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle row click to navigate to payment details
  const handleRowClick = (payment: PaymentTrackingUISearchResultModel) => {
    navigate(`/payment/${payment.aggregateId}`);
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      <h1 style={{ marginBottom: '24px' }}>Search Payments</h1>
      
      {/* Search Form */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '24px', 
        borderRadius: '4px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '24px'
      }}>
        <form onSubmit={handleSearch}>
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'flex-end',
            flexWrap: 'wrap'
          }}>
            {/* Search Type */}
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '14px' 
              }}>
                Search Type
              </label>
              <div style={{ 
                display: 'flex', 
                gap: '16px' 
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="radio"
                    name="searchType"
                    value="client"
                    checked={searchType === 'client'}
                    onChange={() => setSearchType('client')}
                  />
                  Client ID/Reference
                </label>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="radio"
                    name="searchType"
                    value="beneficiary"
                    checked={searchType === 'beneficiary'}
                    onChange={() => setSearchType('beneficiary')}
                  />
                  Beneficiary IBAN/Account
                </label>
              </div>
            </div>
            
            {/* Search Input */}
            <div style={{ flex: 1 }}>
              <label
                htmlFor="searchQuery"
                style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px' 
                }}
              >
                Search Term
              </label>
              <input
                id="searchQuery"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={searchType === 'client' 
                  ? "Enter client ID or reference..." 
                  : "Enter beneficiary IBAN or account number..."}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  width: '100%',
                  fontSize: '16px'
                }}
              />
            </div>
            
            {/* Submit Button */}
            <button
              type="submit"
              style={{
                backgroundColor: '#003366',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '10px 24px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Search
            </button>
          </div>
        </form>
      </div>
      
      {/* Search Guidelines */}
      <div style={{ 
        backgroundColor: '#e3f2fd', 
        padding: '16px', 
        borderRadius: '4px',
        marginBottom: '24px'
      }}>
        <h3 style={{ margin: '0 0 8px 0', color: '#0d47a1' }}>Search Tips</h3>
        <ul style={{ 
          margin: 0, 
          paddingLeft: '24px', 
          color: '#0d47a1',
          fontSize: '14px' 
        }}>
          <li>For client search, enter client ID, name or reference number</li>
          <li>For beneficiary search, enter IBAN or account number</li>
          <li>Results will show all associated payments</li>
          <li>Click on any payment row to view full details</li>
        </ul>
      </div>
      
      {/* Loading State */}
      {isLoading && (
        <div style={{ 
          padding: '24px',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
        }}>
          Searching...
        </div>
      )}
      
      {/* Error State */}
      {error && (
        <div style={{ 
          padding: '16px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px',
          marginBottom: '24px'
        }}>
          {error}
        </div>
      )}
      
      {/* Results */}
      {!isLoading && hasSearched && (
        <div>
          <h2 style={{ marginBottom: '16px' }}>Search Results</h2>
          
          {results.length > 0 ? (
            <DashboardTable 
              data={results}
              onRowClick={handleRowClick}
            />
          ) : (
            <div style={{ 
              padding: '24px',
              textAlign: 'center',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
            }}>
              No payments found for your search criteria.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchPage; 